import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  getMyCrmNotifications,
  markCrmNotificationRead,
  markAllCrmNotificationsRead
} from '../../services/crmApi';
import { useAuth } from '../../contexts/AuthContext';

const POLL_MS = 45000;
const RECENT_UNREAD_MS = 15 * 60 * 1000;

const NotificationBell = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const seenIdsRef = useRef(new Set());
  const bootstrappedRef = useRef(false);
  const panelRef = useRef(null);
  const inFlightRef = useRef(false);
  const userId = user?._id || user?.id || user?.userId;

  const load = useCallback(async ({ announceNew = false } = {}) => {
    if (!isAuthenticated || !userId) return;
    if (inFlightRef.current) return; // Prevent stacked requests that saturate browser connections

    inFlightRef.current = true;
    try {
      const res = await getMyCrmNotifications({ limit: 30 });
      const data = res.data?.data || {};
      const list = data.items || [];
      const count = data.unreadCount || 0;
      const now = Date.now();

      if (!bootstrappedRef.current) {
        const recentUnread = list.filter((n) => {
          if (n.readAt) return false;
          const created = n.createdAt ? new Date(n.createdAt).getTime() : 0;
          return created && now - created <= RECENT_UNREAD_MS;
        });
        recentUnread.slice(0, 3).forEach((n) => {
          toast(n.title || 'New notification', { icon: '🔔', duration: 6000 });
        });
      } else if (announceNew) {
        const fresh = list.filter((n) => !n.readAt && !seenIdsRef.current.has(String(n._id)));
        fresh.slice(0, 3).forEach((n) => {
          toast(n.title || 'New notification', { icon: '🔔', duration: 6000 });
        });
      }

      list.forEach((n) => seenIdsRef.current.add(String(n._id)));
      bootstrappedRef.current = true;
      setItems(list);
      setUnreadCount(count);
    } catch {
      // Ignore polling errors (e.g. permission / offline)
    } finally {
      inFlightRef.current = false;
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    load({ announceNew: false });
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') {
        load({ announceNew: true });
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        load({ announceNew: false });
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [load]);

  useEffect(() => {
    const handleUpdate = () => load({ announceNew: true });
    window.addEventListener('crm-notification-update', handleUpdate);
    return () => window.removeEventListener('crm-notification-update', handleUpdate);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const onOpen = async () => {
    setOpen((v) => !v);
    await load({ announceNew: false });
  };

  const onRead = async (n) => {
    try {
      if (!n.readAt) {
        await markCrmNotificationRead(n._id);
        setItems((prev) => prev.map((x) => (x._id === n._id ? { ...x, readAt: new Date().toISOString() } : x)));
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    } catch {
      // ignore
    }
  };

  const onReadAll = async () => {
    try {
      await markAllCrmNotificationsRead();
      setItems((prev) => prev.map((x) => ({ ...x, readAt: x.readAt || new Date().toISOString() })));
      setUnreadCount(0);
    } catch {
      // ignore
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={panelRef}>
      <button
        type="button"
        onClick={onOpen}
        className="relative p-1.5 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
        aria-label="Notifications"
      >
        <BellIcon className="w-5 h-5 text-gray-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed left-2 right-2 top-14 sm:absolute sm:left-auto sm:right-0 sm:top-auto mt-0 sm:mt-2 sm:w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <span className="text-sm font-semibold text-gray-900">Notifications</span>
            {unreadCount > 0 && (
              <button type="button" onClick={onReadAll} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
            {!items.length && (
              <p className="px-3 py-8 text-sm text-gray-500 text-center">No notifications</p>
            )}
            {items.map((n) => (
              <div
                key={n._id}
                className={`p-3 hover:bg-gray-50 transition-colors cursor-pointer ${!n.readAt ? 'bg-indigo-50/40' : ''}`}
                onClick={() => {
                  onRead(n);
                  if (n.link) {
                    navigate(n.link);
                    setOpen(false);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-1.5">
                  <div className="text-sm font-semibold text-gray-900 leading-snug break-words">
                    {n.type === 'lead_alert' && (
                      <span className="inline-block mr-1 text-xs">🎯</span>
                    )}
                    {n.title}
                  </div>
                  {!n.readAt && (
                    <span className="w-2 h-2 rounded-full bg-indigo-600 flex-shrink-0 mt-1" title="Unread" />
                  )}
                </div>
                <div className="text-xs text-gray-600 mt-1 line-clamp-3 leading-relaxed break-words">{n.message}</div>
                <div className="flex items-center justify-between mt-2 pt-1 border-t border-gray-100/60">
                  <span className="text-[10px] text-gray-400">
                    {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
                  </span>
                  {n.link && (
                    <span className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
                      Open &rarr;
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
